import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import EnumerationListBoxViewModelType from '@/ViewModels/Controls/EnumerationListBoxViewModel';
import EnumerationListBoxType from '@/Controls/EnumerationListBox';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import CheckBoxViewModelType from '@/ViewModels/Controls/CheckBoxViewModel';
import SelectBoxViewModelType from '@/ViewModels/Controls/SelectBoxViewModel';
import SelectBoxItemViewModelType from '@/ViewModels/Controls/SelectBoxItemViewModel';

export default class EnumerationListBoxFactory extends ControlFactory {

    private EnumerationListBoxViewModel = ComponentManager.getViewModel<typeof EnumerationListBoxViewModelType>('EnumerationListBoxViewModel');
    private EnumerationListBox = ComponentManager.getControl<typeof EnumerationListBoxType>('EnumerationListBox');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    private CheckBoxViewModel = ComponentManager.getViewModel<typeof CheckBoxViewModelType>('CheckBoxViewModel');
    private SelectBoxViewModel = ComponentManager.getViewModel<typeof SelectBoxViewModelType>('SelectBoxViewModel');
    private SelectBoxItemViewModel = ComponentManager.getViewModel<typeof SelectBoxItemViewModelType>('SelectBoxItemViewModel');

    private jQueryControl: JQuery<HTMLElement>;
    private sortAlphabetically: KnockoutObservable<boolean>;
    /**
     * @override
     */
    public viewModel: EnumerationListBoxViewModelType;

    public constructor(params) {
        super(params);

        this.sortAlphabetically = this.observableOrDefault(params.sortAlphabetically, false);
    }

    /**
     * @override
     */
    control: EnumerationListBoxType;

    /**
     * @override
     */
    public getControl() {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.viewModel = new this.EnumerationListBoxViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            selectBox: new this.SelectBoxViewModel({
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.select',
                value: this.context.genericObject.getObservable(this.detailViewItem.name),
                items: this.defineComputed(() => {
                    let result = [];

                    for (let enumeration in this.detailViewItem.possiblePDEnumerationValues) {
                        let caption = ResourceManager.getInstance().get(this.detailViewItem.possiblePDEnumerationValues[enumeration]);

                        result.push(new this.SelectBoxItemViewModel({
                            parentLocator: this.locator,
                            locator: this.detailViewItem.name + '.select.[' + enumeration + ']',
                            value: enumeration,
                            checkBox: new this.CheckBoxViewModel({
                                isFocusable: this.isFocusable,
                                parentLocator: this.locator,
                                locator: this.detailViewItem.name + '.select.[' + enumeration + '].checkbox',
                                label: new this.LabelViewModel({
                                    parentLocator: this.locator,
                                    locator: this.detailViewItem.name + '.select.[' + enumeration + '].checkbox.label',
                                    caption: caption
                                })
                            })
                        }));
                    }

                    if (this.sortAlphabetically()) {
                        result = result.sort((a: SelectBoxItemViewModelType, b: SelectBoxItemViewModelType) => {
                            let aCaption = a.checkBox.label.caption();
                            let bCaption = b.checkBox.label.caption();

                            return aCaption.localeCompare(bCaption);
                        });
                    }

                    return result;
                }),
                isEnabled: this.isEnabled,
                tooltip: new this.LabelViewModel({
                    ergName: this.detailViewItem.tooltip
                }),
                isValid: this.isValid,
                invalidationText: this.invalidationText
            })
        });

        this.control = new this.EnumerationListBox(this.viewModel);

        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.addOverwritableToControl(this.jQueryControl, [this.detailViewItem.name]);
        this.registerDefaultValidation([this.detailViewItem.name]);

        return this.jQueryControl;
    }

    public getValueForFilter() {
        let ret = {};
        ret[this.detailViewItem.name] = this.context.genericObject.getValue(this.detailViewItem.name);
        return ret;
    }

    public getFilterInfo(): { field: string, value: string, nullCheck: false } {
        return {
            field: this.detailViewItem.name + '.Value',
            value: this.viewModel?.selectBox.value() || '',
            nullCheck: false
        };
    }
}

ComponentManager.setFactory('EnumerationListBox', EnumerationListBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');